import React, { useState, useEffect, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import {
  Play,
  Volume2,
  Clock,
  CheckCircle2,
  XCircle,
  BookOpen,
  RotateCcw,
  Sparkles,
  Flame,
  Trophy,
  ArrowRight,
  Sliders,
  Shield,
  GraduationCap,
  Swords,
  Send,
  Link2,
  Share2,
  Target,
  BarChart3,
  Check,
  Flag,
} from 'lucide-react';
import {
  BIBLE_CATEGORIES,
  QUESTION_FORMATS,
  DIFFICULTY_LEVELS,
  VERIFIED_SEED_QUESTIONS,
} from '../data/verifiedQuestions.ts';
import { AdSenseBanner } from './AdSenseBanner.tsx';
import { TrophyBadge } from './TrophyBadge.tsx';
import { getTrophyTier } from '../utils/trophyTier.ts';
import { formatAchievementName } from '../utils/achievementFormat.ts';
import {
  UI_TRANSLATIONS,
  SupportedLanguage,
  getLocalizedCategoryLabel,
  getLocalizedDifficultyLabel,
  getLocalizedFormatLabel,
} from '../i18n/translations.ts';
import {
  CanonicalQuizFormat,
  MatchingPair,
  QuizPurposeMode,
  mapToCanonicalFormat,
  evaluateQuizAnswer,
  shuffleOptions,
  calculateSessionAccuracy,
  getEvaluationTier,
  formatDisplayNumber,
} from '../game/rules.ts';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { InfoTooltip } from './ui/InfoTooltip.tsx';
import {
  getScoreTooltipText,
  getAccuracyTooltipText,
  getBestStreakTooltipText,
  getXpGainedTooltipText,
} from '../config/scoringConfig.ts';


export interface QuizQuestion {
  id: number;
  questionCode: string;
  category: string;
  bibleBook: string;
  testament: string;
  difficulty: string;
  questionFormat: string;
  canonicalFormat?: CanonicalQuizFormat;
  questionText: string;
  options: string[];
  correctAnswer?: string;
  acceptedAnswers?: string[];
  matchingPairs?: MatchingPair[];
  explanation?: string;
  scriptureReference: string;
}

interface SoloQuizViewProps {
  userUid: string;
  username: string;
  country: string;
  language: SupportedLanguage;
  initialPreset?: {
    mode: string;
    subMode?: string;
    category?: string;
    questionFormat?: string;
    difficulty?: string;
    autoStart?: boolean;
  } | null;
  onSessionCompleted: () => void;
  onSharePrompt?: (
    contentOrPayload:
      | string
      | {
          defaultContent: string;
          badgeCode?: string;
          postType?: string;
          score?: number;
          accuracy?: number;
          bestStreak?: number;
          xpGained?: number;
        },
    badgeCode?: string
  ) => void;
}

export const SoloQuizView: React.FC<SoloQuizViewProps> = ({
  userUid,
  username,
  country,
  language,
  initialPreset,
  onSessionCompleted,
  onSharePrompt,
}) => {
  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;

  const [gameMode, setGameMode] = useState<QuizPurposeMode>('challenge');
  const [category, setCategory] = useState(
    initialPreset?.category || 'ALL CATEGORIES'
  );
  const [difficulty, setDifficulty] = useState(
    initialPreset?.difficulty || 'Mixed Difficulty'
  );
  const [questionFormat, setQuestionFormat] = useState(
    initialPreset?.questionFormat || 'Automatic / Mixed'
  );
  const [questionCount, setQuestionCount] = useState(5);
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [timerSeconds, setTimerSeconds] = useState(20);
  const [narrationEnabled, setNarrationEnabled] = useState(
    soundEngine.narrationEnabled
  );
  const [narrateInstructions, setNarrateInstructions] = useState(
    soundEngine.narrateInstructions
  );
  const [narrateChoices, setNarrateChoices] = useState(
    soundEngine.narrateChoices
  );
  const [forceFreshBatch, setForceFreshBatch] = useState(false);
  const [reportedQuestionIds, setReportedQuestionIds] = useState<Set<number>>(new Set());
  const [reportToast, setReportToast] = useState<string | null>(null);

  const getAnonSessionId = (): string => {
    let anonId = localStorage.getItem('qb_anon_session_id');
    if (!anonId) {
      anonId = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem('qb_anon_session_id', anonId);
    }
    return anonId;
  };

  const handleReportQuestion = async (qId: number) => {
    if (reportedQuestionIds.has(qId)) return;

    setReportedQuestionIds((prev) => new Set([...prev, qId]));
    setReportToast('Question reported. Thank you for keeping Scripture verified!');
    setTimeout(() => setReportToast(null), 3500);

    try {
      const activeUid = userUid || getAnonSessionId();
      await fetch('/api/quiz/report-question', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId: qId, userUid: activeUid, reason: 'Flagged in game' }),
      });
    } catch {
      // Ignore
    }
  };

  const [phase, setPhase] = useState<
    'setup' | 'loading' | 'playing' | 'results'
  >('setup');
  const [activeSessionConfig, setActiveSessionConfig] = useState<{
    gameMode: QuizPurposeMode;
    category: string;
    difficulty: string;
    questionFormat: string;
    questionCount: number;
    timerEnabled: boolean;
    timerSeconds: number;
    voiceNarration: boolean;
    subMode?: string;
  }>({
    gameMode: 'practice',
    category: 'ALL CATEGORIES',
    difficulty: 'Mixed Difficulty',
    questionFormat: 'Automatic / Mixed',
    questionCount: 5,
    timerEnabled: false,
    timerSeconds: 20,
    voiceNarration: true,
  });
  const [sessionCode, setSessionCode] = useState('');
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);

  // Interactive states for Fill-in-the-Blank, Short Answer, Matching, and Open-Ended
  const [typedAnswerInput, setTypedAnswerInput] = useState('');
  const [fillBlankInputMode, setFillBlankInputMode] = useState<
    'word_bank' | 'type'
  >('word_bank');
  const [selectedLeftMatch, setSelectedLeftMatch] = useState<string | null>(
    null
  );
  const [userMatchedPairs, setUserMatchedPairs] = useState<
    Record<string, string>
  >({});

  const [answerFeedback, setAnswerFeedback] = useState<{
    isCorrect: boolean;
    correctAnswer: string;
    explanation: string;
    scriptureReference: string;
    pointsEarned: number;
    speedBonus: number;
    streakBonus: number;
  } | null>(null);

  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [timeoutCount, setTimeoutCount] = useState(0);
  const [historyItems, setHistoryItems] = useState<
    Array<{
      questionText: string;
      submitted: string;
      correctAnswer: string;
      isCorrect: boolean;
      scriptureReference: string;
      points: number;
    }>
  >([]);
  const [completionSummary, setCompletionSummary] = useState<{
    score?: number;
    xpGained: number;
    accuracy?: number;
    correctCount?: number;
    skippedCount?: number;
    timeoutCount?: number;
    totalQuestions?: number;
    bestStreak?: number;
    evaluation?: any;
    newlyUnlocked: string[];
    countsForRanking?: boolean;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const questionStartRef = useRef<number>(Date.now());

  // Enforce strict timer rules in Ranked Mode
  const handleSelectGameMode = (nextMode: QuizPurposeMode) => {
    soundEngine.playClick();
    setGameMode(nextMode);
    if (nextMode === 'ranked') {
      setTimerEnabled(true);
      setTimerSeconds(20);
      if (questionFormat === 'Open-Ended Reflection') {
        setQuestionFormat('Automatic / Mixed');
      }
    } else if (nextMode === 'practice') {
      setTimerEnabled(false);
    } else {
      setTimerEnabled(true);
      if (questionFormat === 'Open-Ended Reflection') {
        setQuestionFormat('Automatic / Mixed');
      }
    }
  };

  useEffect(() => {
    if (initialPreset) {
      if (initialPreset.category) setCategory(initialPreset.category);
      if (initialPreset.questionFormat)
        setQuestionFormat(initialPreset.questionFormat);
      if (initialPreset.difficulty) setDifficulty(initialPreset.difficulty);
      if (initialPreset.autoStart) {
        startQuizSession({
          overrideCategory: initialPreset.category || 'ALL CATEGORIES',
          overrideFormat: initialPreset.questionFormat || 'Automatic / Mixed',
          overrideDifficulty: initialPreset.difficulty || 'Mixed Difficulty',
          overrideMode: initialPreset.mode || gameMode,
          overrideSubMode: initialPreset.subMode || 'standard',
        });
      }
    }
  }, [initialPreset]);

  // Support direct inspection (?test_results=1 or ?results=1) for deterministic verification
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('test_results') === '1' || searchParams.get('results') === '1') {
        const reqTotal = searchParams.get('total') ? Math.max(1, Number(searchParams.get('total'))) : 10;
        const dummyQuestions = Array.from({ length: reqTotal }, (_, idx) => ({
          ...(VERIFIED_SEED_QUESTIONS[idx % VERIFIED_SEED_QUESTIONS.length] as unknown as QuizQuestion),
          id: idx + 1,
        }));
        setQuestions(dummyQuestions);
        const targetAcc = searchParams.get('accuracy') !== null ? Number(searchParams.get('accuracy')) : 80;
        const correct = searchParams.get('correct') !== null
          ? Number(searchParams.get('correct'))
          : Math.round((targetAcc / 100) * reqTotal);
        const skipped = searchParams.get('skipped') !== null ? Number(searchParams.get('skipped')) : 0;
        const timeouts = searchParams.get('timeouts') !== null ? Number(searchParams.get('timeouts')) : 0;
        const computedScore = searchParams.get('score') !== null
          ? Number(searchParams.get('score'))
          : correct * 125;
        const computedXp = searchParams.get('xp') !== null
          ? Number(searchParams.get('xp'))
          : Math.round(computedScore / 10);

        setCorrectCount(correct);
        setSkippedCount(skipped);
        setTimeoutCount(timeouts);
        setScore(computedScore);
        setMaxStreak(correct > 0 ? Math.min(correct, 5) : 0);
        setHistoryItems([
          {
            questionText: 'In the beginning was the Word, and the Word was with God, and the Word was God.',
            submitted: 'John 1:1',
            correctAnswer: 'John 1:1',
            isCorrect: correct > 0,
            scriptureReference: 'John 1:1',
            points: correct > 0 ? 135 : 0,
          },
        ]);
        const testModeParam = (searchParams.get('mode') as QuizPurposeMode) || 'challenge';
        setGameMode(testModeParam);
        setActiveSessionConfig({
          gameMode: testModeParam,
          category: searchParams.get('category') || 'ALL CATEGORIES',
          difficulty: 'Mixed Difficulty',
          questionFormat: 'Automatic / Mixed',
          questionCount: reqTotal,
          timerEnabled: true,
          timerSeconds: 20,
          voiceNarration: true,
        });
        setCompletionSummary({
          score: computedScore,
          xpGained: computedXp,
          correctCount: correct,
          skippedCount: skipped,
          timeoutCount: timeouts,
          totalQuestions: reqTotal,
          newlyUnlocked: ['BIBLE_SCHOLAR'],
          countsForRanking: reqTotal >= 3,
        });
        setPhase('results');
      }
    } catch {
      // Ignore in non-browser context
    }
  }, []);

  useEffect(() => {
    if (
      phase !== 'playing' ||
      !timerEnabled ||
      selectedAnswer !== null ||
      answerFeedback !== null
    ) {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleAnswerSubmit('__TIMEOUT__');
          return 0;
        }
        if (prev <= 5) {
          soundEngine.playTick(true);
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, timerEnabled, selectedAnswer, answerFeedback, currentIndex]);

  const resetQuestionInteractiveState = () => {
    setSelectedAnswer(null);
    setAnswerFeedback(null);
    setTypedAnswerInput('');
    setFillBlankInputMode('word_bank');
    setSelectedLeftMatch(null);
    setUserMatchedPairs({});
  };

  const getInstructionForQuestion = (q: QuizQuestion) => {
    const canon =
      q.canonicalFormat || mapToCanonicalFormat(q.questionFormat || '');
    switch (canon) {
      case 'true_false':
        return t.trueFalsePrompt;
      case 'fill_blank':
        return t.fillBlankPrompt;
      case 'short_answer':
        return t.shortAnswerPrompt;
      case 'matching':
        return t.matchingPrompt;
      case 'open_ended':
        return t.openEndedPrompt;
      default:
        return t.selectAnswerPrompt;
    }
  };

  const triggerQuestionNarration = (q: QuizQuestion, force = false) => {
    const canon =
      q.canonicalFormat || mapToCanonicalFormat(q.questionFormat || '');
    const choicesToSpeak =
      canon === 'multiple_choice' ||
      canon === 'true_false' ||
      canon === 'fill_blank'
        ? q.options
        : undefined;

    soundEngine.narrateQuizQuestion({
      questionText: q.questionText,
      instructionText: getInstructionForQuestion(q),
      options: choicesToSpeak,
      language,
      force,
    });
  };

  const startQuizSession = async (opts?: {
    overrideCategory?: string;
    overrideFormat?: string;
    overrideDifficulty?: string;
    overrideMode?: string;
    overrideSubMode?: string;
    overrideCount?: number;
    overrideTimer?: boolean;
    overrideTimerSeconds?: number;
    overrideNarration?: boolean;
  }) => {
    soundEngine.playClick();
    setErrorMessage(null);
    setPhase('loading');
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setCorrectCount(0);
    setSkippedCount(0);
    setTimeoutCount(0);
    setHistoryItems([]);
    setCompletionSummary(null);
    resetQuestionInteractiveState();
    soundEngine.resetSessionSounds();

    const activeMode = (opts?.overrideMode as QuizPurposeMode) || gameMode;
    const reqCategory = opts?.overrideCategory || category;
    const reqDifficulty = opts?.overrideDifficulty || difficulty;
    const reqFormat = opts?.overrideFormat || questionFormat;
    const reqCount = opts?.overrideCount ?? questionCount;
    const reqTimer = opts?.overrideTimer ?? timerEnabled;
    const reqTimerSec = opts?.overrideTimerSeconds ?? timerSeconds;
    const reqNarration = opts?.overrideNarration ?? narrationEnabled;
    const reqSubMode = opts?.overrideSubMode || 'standard';

    // Store active session settings snapshot
    setActiveSessionConfig({
      gameMode: activeMode,
      category: reqCategory,
      difficulty: reqDifficulty,
      questionFormat: reqFormat,
      questionCount: reqCount,
      timerEnabled: reqTimer,
      timerSeconds: reqTimerSec,
      voiceNarration: reqNarration,
      subMode: reqSubMode,
    });

    setTimerEnabled(reqTimer);
    setTimerSeconds(reqTimerSec);
    setNarrationEnabled(reqNarration);

    try {
      let sessionCodeVal = '';
      let loadedQuestions: QuizQuestion[] = [];

      try {
        const activeUserUid = userUid || getAnonSessionId();
        const res = await fetch('/api/quiz/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userUid: activeUserUid,
            category: reqCategory,
            difficulty: reqDifficulty,
            questionFormat: reqFormat,
            count: reqCount,
            language,
            mode: activeMode,
            subMode: reqSubMode,
            timerEnabled: reqTimer,
            timerSeconds: reqTimerSec,
            forceAiGeneration: forceFreshBatch,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.questions) && data.questions.length > 0) {
            sessionCodeVal = data.sessionCode || `SES-${Date.now()}`;
            loadedQuestions = data.questions;
          }
        }
      } catch {
        // Fallback to client-side canonical verified questions if network is unreachable
      }

      if (loadedQuestions.length === 0) {
        const isMixed =
          !reqFormat ||
          reqFormat === 'Automatic / Mixed' ||
          reqFormat === 'Mixed Formats' ||
          reqFormat === 'Mixed Challenge';
        const targetCanon = mapToCanonicalFormat(reqFormat || 'Automatic / Mixed');

        const scored = VERIFIED_SEED_QUESTIONS.map((item, idx) => {
          let prio = 0;
          if (
            reqCategory !== 'ALL CATEGORIES' &&
            (item.category.toLowerCase() === reqCategory.toLowerCase() ||
              item.bibleBook.toLowerCase() === reqCategory.toLowerCase())
          ) {
            prio += 50;
          }
          if (
            reqDifficulty !== 'Mixed Difficulty' &&
            item.difficulty.toLowerCase() === reqDifficulty.toLowerCase()
          ) {
            prio += 30;
          }
          if (!isMixed && mapToCanonicalFormat(item.questionFormat) === targetCanon) {
            prio += 60;
          }
          return { item, idx, prio: prio + Math.random() * 5 };
        }).sort((a, b) => b.prio - a.prio);

        const selected = scored.slice(0, Math.max(1, reqCount));
        loadedQuestions = selected.map(({ item, idx }) => {
          const loc = item[language] || item.en;
          const canon = isMixed
            ? mapToCanonicalFormat(item.questionFormat)
            : targetCanon;
          return {
            id: idx + 1,
            questionCode: item.questionCode,
            category: reqCategory !== 'ALL CATEGORIES' ? reqCategory : item.category,
            bibleBook: item.bibleBook,
            testament: item.testament,
            difficulty:
              reqDifficulty !== 'Mixed Difficulty' ? reqDifficulty : item.difficulty,
            questionFormat: isMixed ? item.questionFormat : reqFormat,
            canonicalFormat: canon,
            questionText: loc.questionText,
            options:
              canon === 'multiple_choice' || canon === 'fill_blank'
                ? shuffleOptions(loc.options)
                : loc.options,
            correctAnswer: loc.correctAnswer,
            acceptedAnswers: loc.acceptedAnswers || [],
            matchingPairs: loc.matchingPairs || [],
            explanation: loc.explanation,
            scriptureReference: item.scriptureReference,
          };
        });
        sessionCodeVal = `SES-LOCAL-${Date.now()}`;
      }

      if (!loadedQuestions || loadedQuestions.length === 0) {
        throw new Error(
          'No questions are available for the selected category, difficulty, and quiz type. Please try a different filter.'
        );
      }

      setSessionCode(sessionCodeVal);
      setQuestions(loadedQuestions);
      setCurrentIndex(0);
      resetQuestionInteractiveState();
      setTimeLeft(reqTimerSec);
      questionStartRef.current = Date.now();
      setPhase('playing');

      if (reqNarration && loadedQuestions[0]) {
        triggerQuestionNarration(loadedQuestions[0], false);
      }
    } catch (err: any) {
      setErrorMessage(
        err.message ||
          'No questions are available for the selected configuration. Please try another category or quiz type.'
      );
      setPhase('setup');
    }
  };

  const handlePlayAgain = () => {
    startQuizSession({
      overrideCategory: activeSessionConfig.category,
      overrideDifficulty: activeSessionConfig.difficulty,
      overrideFormat: activeSessionConfig.questionFormat,
      overrideMode: activeSessionConfig.gameMode,
      overrideSubMode: activeSessionConfig.subMode,
      overrideCount: activeSessionConfig.questionCount,
      overrideTimer: activeSessionConfig.timerEnabled,
      overrideTimerSeconds: activeSessionConfig.timerSeconds,
      overrideNarration: activeSessionConfig.voiceNarration,
    });
  };

  const getModeMeta = (mode: QuizPurposeMode) => {
    switch (mode) {
      case 'challenge':
        return {
          label: t.modeChallenge,
          Icon: Swords,
        };
      case 'ranked':
        return {
          label: t.modeRanked,
          Icon: Shield,
        };
      case 'practice':
      default:
        return {
          label: t.modePractice,
          Icon: GraduationCap,
        };
    }
  };

  const handleAnswerSubmit = async (option: string) => {
    if (selectedAnswer !== null || answerFeedback !== null) return;
    const currentQ = questions[currentIndex];
    if (!currentQ) return;

    setSelectedAnswer(option);
    if (option === '__SKIPPED__') {
      setSkippedCount((prev) => prev + 1);
    } else if (option === '__TIMEOUT__') {
      setTimeoutCount((prev) => prev + 1);
    }
    const elapsedMs = Math.max(200, Date.now() - questionStartRef.current);

    try {
      const res = await fetch('/api/quiz/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionCode,
          userUid,
          questionId: currentQ.id,
          submittedAnswer: option,
          timeTakenMs: elapsedMs,
          currentStreak: streak,
          timerEnabled,
          timerSeconds,
          language,
          gameMode,
        }),
      });

      const data = await res.json();
      const isRight = Boolean(data.isCorrect);
      const pts = data.scoreResult?.totalPoints || 0;
      const newStreakVal = data.scoreResult?.newStreak || 0;

      const sfxKey = `solo-${sessionCode || 'session'}-q${currentIndex}`;
      if (isRight) {
        soundEngine.playCorrect(sfxKey);
      } else {
        soundEngine.playWrong(sfxKey);
      }

      setScore((prev) => prev + pts);
      setStreak(newStreakVal);
      setMaxStreak((prev) => Math.max(prev, newStreakVal));
      if (isRight) {
        setCorrectCount((prev) => prev + 1);
      }

      setAnswerFeedback({
        isCorrect: isRight,
        correctAnswer: data.correctAnswer,
        explanation: data.explanation,
        scriptureReference: data.scriptureReference,
        pointsEarned: pts,
        speedBonus: data.scoreResult?.speedBonus || 0,
        streakBonus: data.scoreResult?.streakBonus || 0,
      });

      let displaySubmitted =
        option === '__TIMEOUT__'
          ? t.timeoutFeedback
          : option === '__SKIPPED__'
          ? 'Skipped'
          : option;
      if (displaySubmitted.startsWith('{')) {
        try {
          const parsedPairs = JSON.parse(displaySubmitted);
          displaySubmitted = Object.entries(parsedPairs)
            .map(([l, r]) => `${l} → ${r}`)
            .join(', ');
        } catch {
          // Keep as is
        }
      }

      setHistoryItems((prev) => [
        ...prev,
        {
          questionText: currentQ.questionText,
          submitted: displaySubmitted,
          correctAnswer: data.correctAnswer,
          isCorrect: isRight,
          scriptureReference: data.scriptureReference,
          points: pts,
        },
      ]);

      if (narrationEnabled && data.explanation) {
        const lead = isRight ? t.correctFeedback : t.incorrectFeedback;
        soundEngine.speakText(`${lead} ${data.explanation}`, {
          language,
          force: false,
        });
      }
    } catch {
      const fallbackCorrect = currentQ.correctAnswer || currentQ.options[0] || '';
      const isSkipOrTimeout = option === '__TIMEOUT__' || option === '__SKIPPED__';
      const evalLocal = isSkipOrTimeout
        ? false
        : evaluateQuizAnswer({
            questionFormat: currentQ.questionFormat,
            submittedAnswer: option,
            correctAnswer: fallbackCorrect,
            acceptedAnswers: currentQ.acceptedAnswers,
            matchingPairs: currentQ.matchingPairs,
            gameMode,
          });
      const isCorrect = Boolean(evalLocal);
      const sfxKey = `solo-${sessionCode || 'session'}-q${currentIndex}`;
      if (isCorrect) {
        soundEngine.playCorrect(sfxKey);
        setCorrectCount((prev) => prev + 1);
      } else {
        soundEngine.playWrong(sfxKey);
        setStreak(0);
      }
      setAnswerFeedback({
        isCorrect,
        correctAnswer: fallbackCorrect,
        explanation:
          currentQ.explanation ||
          `${t.scriptureReference}: ${currentQ.scriptureReference}`,
        scriptureReference: currentQ.scriptureReference,
        pointsEarned: 0,
        speedBonus: 0,
        streakBonus: 0,
      });
    }
  };

  const handleNextOrFinish = async () => {
    soundEngine.stopNarration();

    if (currentIndex + 1 < questions.length) {
      soundEngine.playClick();
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      resetQuestionInteractiveState();
      setTimeLeft(timerSeconds);
      questionStartRef.current = Date.now();

      if (narrationEnabled && questions[nextIdx]) {
        triggerQuestionNarration(questions[nextIdx], false);
      }
    } else {
      setPhase('results');
      soundEngine.playVictoryFanfare(`solo-win-${sessionCode || 'session'}`);
      try {
        confetti({
          particleCount: 70,
          spread: 65,
          origin: { y: 0.6 },
        });
      } catch {
        // Ignore confetti errors in headless mode
      }

      try {
        const res = await fetch('/api/quiz/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionCode,
            userUid,
            username,
            country,
            mode: initialPreset?.mode || gameMode,
            totalQuestions: questions.length,
            language,
          }),
        });
        if (res.ok) {
          const summary = await res.json();
          if (typeof summary.score === 'number') {
            setScore(summary.score);
          }
          if (typeof summary.correctCount === 'number') {
            setCorrectCount(summary.correctCount);
          }
          if (typeof summary.skippedCount === 'number') {
            setSkippedCount(summary.skippedCount);
          }
          if (typeof summary.timeoutCount === 'number') {
            setTimeoutCount(summary.timeoutCount);
          }
          if (typeof summary.bestStreak === 'number') {
            setMaxStreak(summary.bestStreak);
          }
          setCompletionSummary(summary);
          onSessionCompleted();
        }
      } catch {
        // Ignore
      }
    }
  };

  // Shuffled right-hand side options for Matching questions
  const currentMatchingData = useMemo(() => {
    const q = questions[currentIndex];
    if (!q) return { leftItems: [] as string[], rightItems: [] as string[] };
    const pairs = q.matchingPairs || [];
    if (pairs.length > 0) {
      return {
        leftItems: pairs.map((p) => p.left),
        rightItems: shuffleOptions(pairs.map((p) => p.right)),
      };
    }
    return { leftItems: [] as string[], rightItems: [] as string[] };
  }, [questions, currentIndex]);

  if (phase === 'loading') {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl overflow-hidden mb-6 shadow-xl border border-amber-500/30 animate-pulse">
          <img
            src="/quickibible-icon.png"
            alt="QuickiBible"
            className="w-full h-full object-contain"
          />
        </div>
        <h2 className="text-2xl font-bold text-[#FAFAFA] mb-2">
          {t.generatingAiQuestions}
        </h2>
        <p className="text-sm text-[#737373] text-[#A3A3A3] max-w-md mx-auto">
          {t.freshPoolDesc} · {getLocalizedCategoryLabel(category, language)}
        </p>
        <div className="mt-8 max-w-md mx-auto space-y-3">
          <div className="h-12 rounded-xl bg-[#1C1C1C] bg-[#1C1C1C] animate-pulse" />
          <div className="h-12 rounded-xl bg-[#1C1C1C] bg-[#1C1C1C] animate-pulse" />
          <div className="h-12 rounded-xl bg-[#1C1C1C] bg-[#1C1C1C] animate-pulse" />
        </div>
      </div>
    );
  }

  if (phase === 'playing' && questions[currentIndex]) {
    const q = questions[currentIndex];
    const canonicalFmt =
      q.canonicalFormat || mapToCanonicalFormat(q.questionFormat || '');
    const progressPercent = Math.round(
      ((currentIndex + 1) / questions.length) * 100
    );
    const isRevealed = answerFeedback !== null;

    return (
      <div
        className="max-w-4xl mx-auto py-4 sm:py-6 px-3 sm:px-6"
        data-testid="solo-quiz-playing"
      >
        {/* Top Quiz HUD Bar - Responsive 2-tier layout */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 mb-5 border-b border-[#262626]">
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-[#737373] text-[#A3A3A3]">
            <span className="font-mono text-[#FAFAFA] font-bold bg-[#141414] text-[#FAFAFA] px-2.5 py-1 rounded-lg">
              {t.questionPrefix} {currentIndex + 1} / {questions.length}
            </span>
            <span className="truncate max-w-[160px] sm:max-w-none">
              {getLocalizedCategoryLabel(q.category, language)}
            </span>
            <span aria-hidden="true">·</span>
            <span>{getLocalizedDifficultyLabel(q.difficulty, language)}</span>
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 w-full sm:w-auto">
            <div className="text-left sm:text-right">
              <div className="text-[10px] uppercase font-semibold text-[#737373] text-[#A3A3A3]">
                {t.scoreLabel}
              </div>
              <div
                className="text-base sm:text-lg font-bold font-mono tabular-nums text-[#A3A3A3]"
                data-testid="quiz-live-score"
              >
                {score.toLocaleString()}
              </div>
            </div>

            <div className="text-left sm:text-right">
              <div className="text-[10px] uppercase font-semibold text-[#737373] text-[#A3A3A3]">
                {t.streakLabel}
              </div>
              <div className="text-base sm:text-lg font-bold font-mono tabular-nums text-amber-500 flex items-center gap-1">
                <Flame className="w-4 h-4" />
                <span>{streak}</span>
              </div>
            </div>

            {timerEnabled && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#1C1C1C] bg-[#1C1C1C] border border-[#262626] shrink-0">
                <Clock
                  className={`w-4 h-4 ${
                    timeLeft <= 5
                      ? 'text-red-500 animate-bounce'
                      : 'text-[#737373] text-[#A3A3A3]'
                  }`}
                />
                <span
                  className={`font-mono tabular-nums text-sm sm:text-base font-bold ${
                    timeLeft <= 5
                      ? 'text-red-400'
                      : 'text-[#FAFAFA]'
                  }`}
                >
                  {timeLeft}s
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-1.5 bg-[#1C1C1C] bg-[#1C1C1C] rounded-full overflow-hidden mb-5 sm:mb-7">
          <div
            className="h-full bg-[#525252] transition-transform duration-300 origin-left"
            style={{ transform: `scaleX(${progressPercent / 100})` }}
          />
        </div>

        {/* Question Card — Centered & Balanced Layout on Mobile, Tablet & Desktop */}
        <div className="rounded-2xl bg-[#141414] border border-[#262626] p-4 sm:p-7 shadow-sm mb-5 overflow-hidden">
          {/* Card Top Meta Row: Format Badge on Left, Report & Narration Buttons on Right */}
          <div className="flex items-center justify-between gap-2 pb-3.5 mb-4 border-b border-[#262626]">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#1C1C1C] text-[11px] font-semibold text-[#A3A3A3] truncate max-w-[50%]">
              <BookOpen className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {getLocalizedFormatLabel(q.questionFormat, language)}
              </span>
            </div>

            <div className="flex items-center gap-2">

              <button
                type="button"
                onClick={() => handleReportQuestion(q.id)}
                disabled={reportedQuestionIds.has(q.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
                  reportedQuestionIds.has(q.id)
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-400 cursor-default'
                    : 'border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C]'
                }`}
                title="Report question error"
              >
                <Flag className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden xs:inline">
                  {reportedQuestionIds.has(q.id) ? 'Reported' : 'Report'}
                </span>
              </button>

              <button
                type="button"
                onClick={() => triggerQuestionNarration(q, true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[#262626] text-[#FAFAFA] hover:bg-[#1C1C1C] shrink-0 text-xs font-medium transition-colors"
                aria-label="Read question aloud"
                title="Read question aloud (TTS)"
              >
                <Volume2 className="w-4 h-4 text-[#A3A3A3] shrink-0" />
                <span className="hidden xs:inline">{t.enableNarration.split(' ')[0]}</span>
              </button>
            </div>
          </div>

          {reportToast && (
            <div className="mb-3 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs text-center font-medium">
              {reportToast}
            </div>
          )}

          {/* Main Centered Question Container */}
          <div className="w-full py-2 sm:py-3 mb-3">
            <h2
              data-testid="quiz-question-heading"
              className="text-lg sm:text-2xl font-bold text-[#FAFAFA] leading-relaxed text-center max-w-2xl mx-auto break-words"
            >
              {q.questionText}
            </h2>
          </div>

          {/* Centered Instruction Prompt */}
          <div className="text-xs text-[#737373] text-[#A3A3A3] text-center mb-5">
            {getInstructionForQuestion(q)}
          </div>

          {/* FORMAT 1: TRUE OR FALSE */}
          {canonicalFmt === 'true_false' && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-w-xl mx-auto"
              data-testid="quiz-format-true-false"
            >
              {(q.options.length === 2
                ? q.options
                : [t.trueLabel, t.falseLabel]
              ).map((option, idx) => {
                const isSelected = selectedAnswer === option;
                const isRightOption =
                  isRevealed &&
                  option.trim().toLowerCase() ===
                    (answerFeedback?.correctAnswer || '').trim().toLowerCase();
                const isWrongSelected =
                  isRevealed && isSelected && !answerFeedback?.isCorrect;

                let btnStyle =
                  idx === 0
                    ? 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500 text-[#FAFAFA]'
                    : 'border-rose-500/30 bg-rose-500/5 hover:border-rose-500 text-[#FAFAFA]';

                if (isRevealed) {
                  if (isRightOption) {
                    btnStyle =
                      'border-emerald-500 bg-emerald-500/15 text-emerald-300 ring-2 ring-emerald-500/30';
                  } else if (isWrongSelected) {
                    btnStyle =
                      'border-red-500 bg-red-500/15 text-red-300';
                  } else {
                    btnStyle =
                      'border-[#262626] opacity-50 text-[#737373]';
                  }
                }

                return (
                  <button
                    key={idx}
                    type="button"
                    disabled={isRevealed}
                    onClick={() => handleAnswerSubmit(option)}
                    className={`min-h-[64px] p-4 rounded-2xl border-2 font-bold text-base sm:text-lg flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] ${btnStyle}`}
                  >
                    <span>{option}</span>
                    {isRevealed && isRightOption && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    )}
                    {isRevealed && isWrongSelected && (
                      <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* FORMAT 2: FILL IN THE BLANK (Word Selection OR Type Answer) */}
          {canonicalFmt === 'fill_blank' && (
            <div
              className="space-y-4 max-w-2xl mx-auto"
              data-testid="quiz-format-fill-blank"
            >
              {!isRevealed && q.options.length > 0 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFillBlankInputMode('word_bank')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      fillBlankInputMode === 'word_bank'
                        ? 'bg-[#525252] text-white'
                        : 'bg-[#1C1C1C] bg-[#1C1C1C] text-[#D4D4D4]'
                    }`}
                  >
                    {t.wordBankMode}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFillBlankInputMode('type')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      fillBlankInputMode === 'type'
                        ? 'bg-[#525252] text-white'
                        : 'bg-[#1C1C1C] bg-[#1C1C1C] text-[#D4D4D4]'
                    }`}
                  >
                    {t.typeWordMode}
                  </button>
                </div>
              )}

              {fillBlankInputMode === 'word_bank' && q.options.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {q.options.map((option, idx) => {
                    const isSelected = selectedAnswer === option;
                    const isRightOption =
                      isRevealed &&
                      option.trim().toLowerCase() ===
                        (answerFeedback?.correctAnswer || '')
                          .trim()
                          .toLowerCase();
                    const isWrongSelected =
                      isRevealed && isSelected && !answerFeedback?.isCorrect;

                    let btnStyle =
                      'border-[#262626] bg-[#1C1C1C] hover:border-[#525252] text-[#FAFAFA]';
                    if (isRevealed) {
                      if (isRightOption) {
                        btnStyle =
                          'border-emerald-500 bg-emerald-500/10 text-emerald-300 font-semibold';
                      } else if (isWrongSelected) {
                        btnStyle =
                          'border-red-500 bg-red-500/10 text-red-300';
                      } else {
                        btnStyle =
                          'border-[#262626] opacity-55 text-[#737373]';
                      }
                    }

                    return (
                      <button
                        key={idx}
                        type="button"
                        disabled={isRevealed}
                        onClick={() => handleAnswerSubmit(option)}
                        className={`p-3.5 sm:p-4 rounded-xl border text-center font-semibold text-sm sm:text-base transition-all min-h-[52px] flex items-center justify-center gap-2 ${btnStyle}`}
                      >
                        <span className="break-words">{option}</span>
                        {isRevealed && isRightOption && (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                        )}
                        {isRevealed && isWrongSelected && (
                          <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (typedAnswerInput.trim()) {
                      handleAnswerSubmit(typedAnswerInput.trim());
                    }
                  }}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <input
                    type="text"
                    disabled={isRevealed}
                    value={typedAnswerInput}
                    onChange={(e) => setTypedAnswerInput(e.target.value)}
                    placeholder={t.typeAnswerPlaceholder}
                    className="flex-1 rounded-xl border border-[#262626] bg-[#1C1C1C] px-4 py-3 text-sm sm:text-base text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
                  />
                  <button
                    type="submit"
                    disabled={isRevealed || !typedAnswerInput.trim()}
                    className="min-h-[48px] px-6 py-3 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors shrink-0"
                  >
                    <Send className="w-4 h-4" />
                    <span>{t.submitAnswerBtn}</span>
                  </button>
                </form>
              )}
            </div>
          )}

          {/* FORMAT 3: SHORT ANSWER */}
          {canonicalFmt === 'short_answer' && (
            <div
              className="max-w-xl mx-auto space-y-3"
              data-testid="quiz-format-short-answer"
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (typedAnswerInput.trim()) {
                    handleAnswerSubmit(typedAnswerInput.trim());
                  }
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="text"
                  disabled={isRevealed}
                  value={typedAnswerInput}
                  onChange={(e) => setTypedAnswerInput(e.target.value)}
                  placeholder={t.typeAnswerPlaceholder}
                  aria-label={t.typeAnswerPlaceholder}
                  className="flex-1 rounded-xl border border-[#262626] bg-[#1C1C1C] px-4 py-3 text-sm sm:text-base text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252] text-center sm:text-left"
                />
                <button
                  type="submit"
                  disabled={isRevealed || !typedAnswerInput.trim()}
                  className="min-h-[48px] px-6 py-3 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors shrink-0"
                >
                  <Send className="w-4 h-4" />
                  <span>{t.submitAnswerBtn}</span>
                </button>
              </form>
            </div>
          )}

          {/* FORMAT 4: MATCHING (Touch-Friendly Two-Column Tap-to-Match) */}
          {canonicalFmt === 'matching' &&
            currentMatchingData.leftItems.length > 0 && (
              <div
                className="space-y-4 max-w-2xl mx-auto"
                data-testid="quiz-format-matching"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-2.5">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-[#A3A3A3] text-center sm:text-left">
                      1. {t.matchingColumnA}
                    </div>
                    {currentMatchingData.leftItems.map((leftItem, idx) => {
                      const isActive = selectedLeftMatch === leftItem;
                      const matchedRight = userMatchedPairs[leftItem];
                      return (
                        <button
                          key={leftItem}
                          type="button"
                          disabled={isRevealed}
                          onClick={() => {
                            soundEngine.playClick();
                            setSelectedLeftMatch(
                              isActive ? null : leftItem
                            );
                          }}
                          className={`w-full p-3.5 rounded-xl border text-left text-xs sm:text-sm font-semibold transition-all flex items-center justify-between gap-2 min-h-[50px] ${
                            isActive
                              ? 'border-[#525252] bg-[#262626] text-[#FAFAFA] ring-2 ring-[#525252]'
                              : matchedRight
                              ? 'border-emerald-500/50 bg-emerald-500/5 text-[#FAFAFA]'
                              : 'border-[#262626] bg-[#1C1C1C] text-[#FAFAFA]'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-6 h-6 rounded-md bg-[#1C1C1C] text-[#A3A3A3] font-mono text-xs font-bold flex items-center justify-center shrink-0">
                              {idx + 1}
                            </span>
                            <span className="break-words">{leftItem}</span>
                          </div>
                          {matchedRight && (
                            <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 truncate max-w-[120px] shrink-0">
                              → {matchedRight}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-2.5">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-[#A3A3A3] text-center sm:text-left">
                      2. {t.matchingColumnB}
                    </div>
                    {currentMatchingData.rightItems.map((rightItem) => {
                      const pairedLeftEntry = Object.entries(
                        userMatchedPairs
                      ).find(([, rVal]) => rVal === rightItem);
                      const pairedLeftIdx = pairedLeftEntry
                        ? currentMatchingData.leftItems.indexOf(
                            pairedLeftEntry[0]
                          ) + 1
                        : null;

                      return (
                        <button
                          key={rightItem}
                          type="button"
                          disabled={isRevealed}
                          onClick={() => {
                            soundEngine.playClick();
                            const targetLeft =
                              selectedLeftMatch ||
                              currentMatchingData.leftItems.find(
                                (l) => !userMatchedPairs[l]
                              );
                            if (!targetLeft) return;
                            setUserMatchedPairs((prev) => ({
                              ...prev,
                              [targetLeft]: rightItem,
                            }));
                            setSelectedLeftMatch(null);
                          }}
                          className={`w-full p-3.5 rounded-xl border text-left text-xs sm:text-sm font-medium transition-all flex items-center justify-between gap-2 min-h-[50px] ${
                            pairedLeftIdx
                              ? 'border-[#525252] bg-[#1C1C1C] text-[#FAFAFA] font-semibold'
                              : 'border-[#262626] bg-[#141414] hover:border-[#525252] text-[#FAFAFA]'
                          }`}
                        >
                          <span className="break-words">{rightItem}</span>
                          {pairedLeftIdx && (
                            <span className="w-6 h-6 rounded-md bg-[#525252] text-white font-mono text-xs font-bold flex items-center justify-center shrink-0">
                              #{pairedLeftIdx}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {!isRevealed && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setUserMatchedPairs({});
                        setSelectedLeftMatch(null);
                      }}
                      className="px-4 py-2.5 rounded-xl border border-[#262626] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C]"
                    >
                      {t.clearMatchesBtn}
                    </button>

                    <button
                      type="button"
                      disabled={
                        Object.keys(userMatchedPairs).length <
                        currentMatchingData.leftItems.length
                      }
                      onClick={() =>
                        handleAnswerSubmit(JSON.stringify(userMatchedPairs))
                      }
                      className="min-h-[46px] px-6 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-50 text-white text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Link2 className="w-4 h-4" />
                      <span>
                        {t.submitMatchesBtn} (
                        {Object.keys(userMatchedPairs).length}/
                        {currentMatchingData.leftItems.length})
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}

          {/* FORMAT 5: OPEN-ENDED REFLECTION (Practice Mode) */}
          {canonicalFmt === 'open_ended' && (
            <div
              className="max-w-xl mx-auto space-y-3"
              data-testid="quiz-format-open-ended"
            >
              <textarea
                rows={3}
                disabled={isRevealed}
                value={typedAnswerInput}
                onChange={(e) => setTypedAnswerInput(e.target.value)}
                placeholder={t.typeAnswerPlaceholder}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] p-3.5 text-sm text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              />
              {!isRevealed && (
                <button
                  type="button"
                  disabled={typedAnswerInput.trim().length < 2}
                  onClick={() => handleAnswerSubmit(typedAnswerInput.trim())}
                  className="w-full min-h-[46px] py-2.5 px-5 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  <span>{t.submitAnswerBtn}</span>
                </button>
              )}
            </div>
          )}

          {/* FORMAT 6 (DEFAULT): MULTIPLE CHOICE (3–5 randomized options) */}
          {(canonicalFmt === 'multiple_choice' ||
            (canonicalFmt === 'matching' &&
              currentMatchingData.leftItems.length === 0)) && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              data-testid="quiz-format-multiple-choice"
            >
              {q.options.map((option, idx) => {
                const letters = ['A', 'B', 'C', 'D', 'E'];
                const isSelected = selectedAnswer === option;
                const isRightOption =
                  isRevealed &&
                  option.trim().toLowerCase() ===
                    (answerFeedback?.correctAnswer || '').trim().toLowerCase();
                const isWrongSelected =
                  isRevealed && isSelected && !answerFeedback?.isCorrect;

                let btnStyle =
                  'border-[#262626] bg-[#1C1C1C] hover:border-[#525252] text-[#FAFAFA] active:scale-[0.99]';

                if (isRevealed) {
                  if (isRightOption) {
                    btnStyle =
                      'border-emerald-500 bg-emerald-500/10 text-emerald-300 font-semibold';
                  } else if (isWrongSelected) {
                    btnStyle =
                      'border-red-500 bg-red-500/10 text-red-300';
                  } else {
                    btnStyle =
                      'border-[#262626] opacity-55 text-[#737373]';
                  }
                }

                return (
                  <button
                    key={idx}
                    type="button"
                    disabled={isRevealed}
                    onClick={() => handleAnswerSubmit(option)}
                    className={`group flex items-center justify-between gap-3 p-3.5 sm:p-4 rounded-xl border text-left transition-all min-h-[54px] ${btnStyle} ${
                      q.options.length === 3 ||
                      (q.options.length === 5 && idx === 4)
                        ? 'sm:col-span-2'
                        : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-lg bg-[#141414] border border-[#262626] flex items-center justify-center font-mono text-xs font-bold text-[#D4D4D4] shrink-0">
                        {letters[idx] || idx + 1}
                      </span>
                      <span className="text-sm sm:text-base leading-snug break-words">
                        {option}
                      </span>
                    </div>
                    {isRevealed && isRightOption && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    )}
                    {isRevealed && isWrongSelected && (
                      <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Secondary Skip Button below answer options */}
          {!isRevealed && (
            <div className="mt-4 pt-2 flex justify-center">
              <button
                type="button"
                onClick={() => handleAnswerSubmit('__SKIPPED__')}
                data-testid="quiz-skip-btn"
                className="inline-flex items-center gap-1 text-xs font-medium text-[#737373] hover:text-[#A3A3A3] transition-colors cursor-pointer"
                title="Skip question (0 points)"
              >
                <span>Skip question</span>
                <ArrowRight className="w-3 h-3 shrink-0" />
              </button>
            </div>
          )}
        </div>

        {/* Immediate Answer Feedback & Canonical Explanation */}
        {answerFeedback && (
          <div
            className="rounded-2xl border border-[#262626] bg-[#141414] p-5 sm:p-6 shadow-sm"
            role="region"
            aria-label="Answer explanation"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
              <div className="flex items-center gap-2.5">
                {answerFeedback.isCorrect ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500 shrink-0" />
                )}
                <span className="text-base sm:text-lg font-bold text-[#FAFAFA] break-words">
                  {answerFeedback.isCorrect
                    ? `${t.correctFeedback} +${answerFeedback.pointsEarned.toLocaleString()} ${t.scoreLabel.toLowerCase()}`
                    : `${t.incorrectFeedback} · ${answerFeedback.correctAnswer}`}
                </span>
              </div>

              <div className="text-xs font-mono text-[#A3A3A3] font-semibold">
                {t.scriptureReference}: {answerFeedback.scriptureReference}
              </div>
            </div>

            <p className="text-sm text-[#D4D4D4] leading-relaxed mb-5 break-words">
              {answerFeedback.explanation}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 pt-4 border-t border-[#262626]">
              <div className="text-xs text-[#737373] text-[#A3A3A3] font-mono">
                {answerFeedback.isCorrect
                  ? `${t.speedBonus}: +${answerFeedback.speedBonus} · ${t.streakBonus}: +${answerFeedback.streakBonus}`
                  : `${t.streakLabel}: 0`}
              </div>

              <button
                type="button"
                onClick={handleNextOrFinish}
                className="w-full sm:w-auto min-h-[48px] px-6 py-3 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors whitespace-nowrap"
              >
                <span>
                  {currentIndex + 1 < questions.length
                    ? t.nextQuestion
                    : t.finishQuiz}
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (phase === 'results') {
    const questionCountDisplay =
      completionSummary?.totalQuestions ||
      questions.length ||
      activeSessionConfig.questionCount;
    const effectiveCorrectCount =
      completionSummary?.correctCount ?? correctCount;
    const effectiveSkippedCount =
      completionSummary?.skippedCount ?? skippedCount;
    const effectiveTimeoutCount =
      completionSummary?.timeoutCount ?? timeoutCount;
    const sessionAccuracy = calculateSessionAccuracy({
      correctCount: effectiveCorrectCount,
      totalQuestions: questionCountDisplay,
      skippedCount: effectiveSkippedCount,
      timeoutCount: effectiveTimeoutCount,
    });
    const accuracy = completionSummary?.accuracy ?? sessionAccuracy.percent;
    const evalTier =
      completionSummary?.evaluation ??
      getEvaluationTier(accuracy, questionCountDisplay);
    const tier = getTrophyTier(accuracy, questionCountDisplay);
    const authoritativeScore = completionSummary?.score ?? score;
    const gainedXp = completionSummary?.xpGained ?? 0;
    const authoritativeBestStreak = completionSummary?.bestStreak ?? maxStreak;

    const scoreFormatted = formatDisplayNumber(authoritativeScore);
    const xpFormatted = formatDisplayNumber(gainedXp);
    const streakFormatted = formatDisplayNumber(authoritativeBestStreak);

    const playedMode = activeSessionConfig.gameMode;
    const modeMeta = getModeMeta(playedMode);
    const ModeIcon = modeMeta.Icon;
    const categoryLabel = (
      getLocalizedCategoryLabel(activeSessionConfig.category, language) || 'All Categories'
    ).toUpperCase();
    const difficultyLabel =
      getLocalizedDifficultyLabel(activeSessionConfig.difficulty, language) ||
      activeSessionConfig.difficulty;
    const countLabel = `${questionCountDisplay} ${questionCountDisplay === 1 ? 'Question' : 'Questions'}`;

    const tierBadgeColor =
      evalTier.trophy === 'gold'
        ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
        : evalTier.trophy === 'silver'
        ? 'bg-slate-300/15 border-slate-300/40 text-slate-200'
        : evalTier.trophy === 'bronze'
        ? 'bg-amber-700/20 border-amber-600/40 text-amber-400'
        : 'bg-[#1C1C1C] border-[#404040] text-[#A3A3A3]';

    return (
      <div
        className="max-w-5xl mx-auto py-8 px-4 sm:px-6"
        data-testid="solo-quiz-results"
      >
        {/* Main Results Card */}
        <div className="rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 lg:p-10 shadow-2xl mb-8">
          {/* Row 1: Trophy Column (Left ~22%) + Information & Actions Column (Right) */}
          <div className="flex flex-col lg:flex-row items-center lg:items-stretch gap-6 lg:gap-8 pb-8">
            {/* Left Column (~24%): Dynamic Trophy + Tier Badge */}
            <div className="w-full lg:w-[24%] shrink-0 flex flex-col items-center justify-center lg:border-r lg:border-[#262626] lg:pr-8 pb-4 lg:pb-0 gap-3">
              {tier !== 'none' ? (
                <TrophyBadge
                  tier={tier}
                  accuracy={accuracy}
                  className="w-full max-w-[190px] sm:max-w-[210px]"
                />
              ) : (
                <div
                  data-testid="no-trophy-card"
                  className="w-full max-w-[190px] rounded-2xl border border-[#262626] bg-[#1C1C1C] p-6 text-center space-y-2"
                >
                  <BookOpen className="w-10 h-10 text-[#737373] mx-auto" />
                  <div className="text-sm font-bold text-[#FAFAFA]">
                    {evalTier.isRanked ? evalTier.label : 'Unranked Session'}
                  </div>
                  <div className="text-[11px] text-[#737373]">
                    {evalTier.isRanked
                      ? 'Score 40%+ to earn a trophy'
                      : 'Play 3+ questions for tier & ranking'}
                  </div>
                </div>
              )}

              {/* Evaluation Tier Pill */}
              <div
                data-testid="result-evaluation-tier"
                className={`inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full border text-xs font-extrabold tracking-wide uppercase ${tierBadgeColor}`}
              >
                {evalTier.trophy !== 'none' && <Trophy className="w-3.5 h-3.5 shrink-0" />}
                <span>
                  {evalTier.isRanked ? evalTier.label : 'Unranked (< 3 Qs)'}
                </span>
              </div>
            </div>

            {/* Right Column: Eyebrow, Title on ONE line, Accuracy Headline, and Action Buttons */}
            <div className="flex-1 flex flex-col justify-center min-w-0 w-full lg:pl-2">
              <div className="mb-3 text-center lg:text-left space-y-2">
                {/* Line 1: Mode pill with icon */}
                <div className="flex items-center justify-center lg:justify-start gap-2 flex-wrap">
                  <div className="bg-[#1C1C1C] border border-[#404040] text-[#FAFAFA] text-xs font-semibold uppercase tracking-wider rounded-full px-3 py-1 inline-flex items-center gap-1.5 shadow-xs">
                    <ModeIcon className="w-3.5 h-3.5 text-[#F97316] shrink-0" />
                    <span>{modeMeta.label}</span>
                  </div>
                </div>

                {/* Line 2: Category · Difficulty · Question Count */}
                <div className="text-xs tracking-wider">
                  <span className="font-semibold text-[#A3A3A3] uppercase">
                    {categoryLabel}
                  </span>
                  <span className="text-[#737373]">
                    {' · '}
                    {difficultyLabel}
                    {' · '}
                    {countLabel}
                  </span>
                </div>
              </div>

              {/* Title: 'Quiz Session' in white, 'Completed!' in orange */}
              <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-extrabold tracking-tight text-[#FAFAFA] leading-tight mb-2 text-center lg:text-left">
                Quiz Session <span className="text-[#F97316]">Completed!</span>
              </h2>

              {/* Prominent Session Accuracy Display: "8/10 correct" (large) with "80%" beside it (small) */}
              <div
                data-testid="result-accuracy-headline"
                className="mb-5 text-center lg:text-left"
              >
                <div className="inline-flex items-baseline gap-2.5 flex-wrap justify-center lg:justify-start">
                  <span className="text-2xl sm:text-3xl font-extrabold font-mono tabular-nums text-[#10B981]">
                    {sessionAccuracy.primaryLabel}
                  </span>
                  <span className="text-sm sm:text-base font-semibold font-mono tabular-nums text-[#A3A3A3]">
                    {sessionAccuracy.secondaryLabel}
                  </span>
                </div>
                {sessionAccuracy.breakdownText && (
                  <div
                    data-testid="result-accuracy-breakdown"
                    className="text-xs text-[#A3A3A3] mt-1 font-medium"
                  >
                    {sessionAccuracy.breakdownText}
                  </div>
                )}
              </div>

              {/* Button Row: Left-aligned on desktop, stacked full-width on mobile (< 640px) */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-start gap-3 w-full">
                {/* 1. Share to Feed (Order 2 on mobile, Order 1 on sm+) */}
                <button
                  type="button"
                  onClick={() => {
                    const defaultMsg = `Completed a ${getLocalizedCategoryLabel(activeSessionConfig.category, language)} quiz in ${modeMeta.label}: ${sessionAccuracy.primaryLabel} (${sessionAccuracy.secondaryLabel}) · Score ${scoreFormatted.text}! 📖🙏`;
                    const firstUnlocked = completionSummary?.newlyUnlocked?.[0];

                    if (onSharePrompt) {
                      onSharePrompt({
                        defaultContent: defaultMsg,
                        badgeCode: activeSessionConfig.gameMode === 'challenge' ? 'CHALLENGE_MODE' : firstUnlocked,
                        postType: activeSessionConfig.gameMode === 'challenge' ? 'challenge_mode' : 'scripture_milestone',
                        score,
                        accuracy,
                        bestStreak: maxStreak,
                        xpGained: gainedXp,
                      });
                    } else if (navigator.clipboard) {
                      navigator.clipboard.writeText(defaultMsg);
                    }
                  }}
                  className="h-12 min-h-[48px] px-5 py-2.5 rounded-xl border border-[#404040] bg-transparent text-[#FAFAFA] hover:bg-[#1C1C1C] hover:border-[#525252] text-sm font-semibold transition-all inline-flex items-center justify-center gap-2.5 order-2 sm:order-1 whitespace-nowrap active:scale-[0.99] cursor-pointer"
                >
                  <Share2 className="w-4 h-4 text-[#F97316] shrink-0" />
                  <span>Share to Feed</span>
                </button>

                {/* 2. Play Again (Order 1 on mobile, Order 2 on sm+) */}
                <button
                  type="button"
                  onClick={handlePlayAgain}
                  className="h-12 min-h-[48px] px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white text-sm font-semibold transition-all shadow-lg shadow-orange-950/40 inline-flex items-center justify-center gap-2.5 order-1 sm:order-2 whitespace-nowrap active:scale-[0.99] cursor-pointer"
                >
                  <RotateCcw className="w-4 h-4 shrink-0" />
                  <span>{t.playAgain}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Row 2: 4-Column Stats Row with Dividers */}
          <div className="border-t border-[#262626] pt-6 sm:pt-8">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {/* Column 1: Score */}
              <div className="relative flex flex-col items-center justify-center py-3 sm:py-4 px-3 sm:px-4 border-r border-b md:border-b-0 border-[#262626] text-center">
                <div className="absolute top-2 right-2">
                  <InfoTooltip
                    ariaLabel={t.ariaHowScoreCalculated || 'How Score is calculated'}
                    content={getScoreTooltipText(t)}
                  />
                </div>
                <div className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-[#A3A3A3] mb-2">
                  <Target className="w-4 h-4 text-[#A3A3A3] shrink-0" />
                  <span>Score</span>
                </div>
                <div
                  data-testid="result-stat-score"
                  className={`text-2xl sm:text-3xl font-extrabold font-mono tabular-nums ${
                    scoreFormatted.isZero ? 'text-[#737373]' : 'text-[#FAFAFA]'
                  }`}
                >
                  {scoreFormatted.text}
                </div>
              </div>

              {/* Column 2: Accuracy (X/Y correct large + % small) */}
              <div className="relative flex flex-col items-center justify-center py-3 sm:py-4 px-3 sm:px-4 border-b md:border-b-0 md:border-r border-[#262626] text-center">
                <div className="absolute top-2 right-2">
                  <InfoTooltip
                    ariaLabel={t.ariaHowAccuracyCalculated || 'How Accuracy is calculated'}
                    content={getAccuracyTooltipText(t)}
                  />
                </div>
                <div className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-[#A3A3A3] mb-2">
                  <BarChart3 className="w-4 h-4 text-[#A3A3A3] shrink-0" />
                  <span>Accuracy</span>
                </div>
                <div className="inline-flex items-baseline justify-center gap-1.5 flex-wrap">
                  <span className="text-xl sm:text-2xl font-extrabold font-mono tabular-nums text-[#10B981]">
                    {sessionAccuracy.primaryLabel}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold font-mono tabular-nums text-[#A3A3A3]">
                    {sessionAccuracy.secondaryLabel}
                  </span>
                </div>
                {sessionAccuracy.breakdownText && (
                  <div className="text-[11px] text-[#737373] mt-0.5">
                    {sessionAccuracy.breakdownText}
                  </div>
                )}
              </div>

              {/* Column 3: Best Streak */}
              <div className="relative flex flex-col items-center justify-center py-3 sm:py-4 px-3 sm:px-4 border-r border-[#262626] text-center">
                <div className="absolute top-2 right-2">
                  <InfoTooltip
                    ariaLabel={t.ariaHowBestStreakCalculated || 'How Best Streak is calculated'}
                    content={getBestStreakTooltipText(t)}
                  />
                </div>
                <div className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-[#A3A3A3] mb-2">
                  <Flame className="w-4 h-4 text-[#F97316] shrink-0" />
                  <span>Best Streak</span>
                </div>
                <div
                  data-testid="result-stat-streak"
                  className={`text-2xl sm:text-3xl font-extrabold font-mono tabular-nums ${
                    streakFormatted.isZero ? 'text-[#737373]' : 'text-[#F59E0B]'
                  }`}
                >
                  {streakFormatted.text}
                </div>
              </div>

              {/* Column 4: XP Gained */}
              <div className="relative flex flex-col items-center justify-center py-3 sm:py-4 px-3 sm:px-4 text-center">
                <div className="absolute top-2 right-2">
                  <InfoTooltip
                    ariaLabel={t.ariaHowXpCalculated || 'How XP is calculated'}
                    content={getXpGainedTooltipText(t)}
                  />
                </div>
                <div className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-[#A3A3A3] mb-2">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold border border-[#737373] text-[#FAFAFA] leading-none">
                    XP
                  </span>
                  <span>XP Gained</span>
                </div>
                <div
                  data-testid="result-stat-xp"
                  className={`text-2xl sm:text-3xl font-extrabold font-mono tabular-nums ${
                    xpFormatted.isZero ? 'text-[#737373]' : 'text-[#FAFAFA]'
                  }`}
                >
                  {xpFormatted.text === '–' ? xpFormatted.text : `+${xpFormatted.text}`}
                </div>
              </div>

            </div>
          </div>

          {completionSummary?.newlyUnlocked &&
            completionSummary.newlyUnlocked.length > 0 && (
              <div className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-3">
                <Trophy className="w-5 h-5 text-amber-500 shrink-0" />
                <div className="text-sm text-amber-200">
                  <strong>New Achievement Unlocked:</strong>{' '}
                  {completionSummary.newlyUnlocked
                    .map((id) => formatAchievementName(id))
                    .join(', ')}
                </div>
              </div>
            )}

          {/* Question Review List */}
          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-semibold text-[#FAFAFA]">
              {t.scriptureReference} · {t.quizCompletedTitle}
            </h3>
            {historyItems.map((item, i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-[#262626]"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-[#737373] text-[#A3A3A3]">
                    <span className="font-mono">#{i + 1}</span>
                    <span>·</span>
                    <span className="font-mono text-[#A3A3A3]">
                      {item.scriptureReference}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-[#FAFAFA] break-words">
                    {item.questionText}
                  </p>
                  <p className="text-xs text-[#737373] text-[#A3A3A3] break-words">
                    <strong>{item.submitted}</strong>{' '}
                    {!item.isCorrect && (
                      <span>
                        · {t.correctFeedback}:{' '}
                        <strong>{item.correctAnswer}</strong>
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`font-mono text-xs font-bold ${
                      item.isCorrect
                        ? 'text-emerald-400'
                        : 'text-[#737373]'
                    }`}
                  >
                    {item.isCorrect ? `+${formatDisplayNumber(item.points).text}` : '–'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <AdSenseBanner placement="quiz-results" className="mt-6" />
      </div>
    );
  }

  // Filter out Open-Ended Reflection from dropdown when in Challenge or Ranked Mode
  const availableFormats = QUESTION_FORMATS.filter((fmt) =>
    gameMode !== 'practice' ? fmt !== 'Open-Ended Reflection' : true
  );

  return (
    <div className="max-w-6xl mx-auto py-6 sm:py-8 px-4 sm:px-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#F97316] mb-2">
          {t.selectGameMode}
        </div>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-[#FAFAFA] tracking-tight mb-2">
          {t.soloConfigTitle}
        </h1>
        <p className="text-sm sm:text-base text-[#A3A3A3]">
          {t.heroSubtitle}
        </p>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-300"
        >
          {errorMessage}
        </div>
      )}

      {/* GAME PURPOSE / MODE SELECTOR (Practice – Bronze | Challenge – Red | Ranked – Gold) */}
      <div className="mb-8">
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5"
          role="radiogroup"
          aria-label={t.selectGameMode}
        >
          {(
            [
              {
                id: 'practice' as QuizPurposeMode,
                title: t.modePractice,
                desc: t.modePracticeDesc,
                icon: BookOpen,
                cardBg:
                  'bg-[linear-gradient(145deg,#321707_0%,#4A210B_52%,#6B310F_100%)]',
                activeBorder:
                  'border-[#CD7F32] shadow-[0_0_28px_rgba(205,127,50,0.42),inset_0_1px_0_rgba(240,179,109,0.45)]',
                inactiveBorder:
                  'border-[#CD7F32]/75 shadow-[0_0_14px_rgba(205,127,50,0.18)] md:hover:border-[#E69A4C] md:hover:shadow-[0_0_24px_rgba(205,127,50,0.34)]',
                iconContainer:
                  'bg-[linear-gradient(145deg,#5C2A0E_0%,#321707_100%)] border-[#CD7F32]/65 shadow-[inset_0_1px_1px_rgba(240,179,109,0.35),0_4px_12px_rgba(0,0,0,0.35)]',
                iconColor: 'text-[#F0B36D]',
                topGlow:
                  'bg-[radial-gradient(ellipse_at_top_left,rgba(240,179,109,0.28),transparent_65%)]',
                bottomGlow:
                  'bg-[radial-gradient(ellipse_at_bottom_right,rgba(205,127,50,0.32),transparent_65%)]',
              },
              {
                id: 'challenge' as QuizPurposeMode,
                title: t.modeChallenge,
                desc: t.modeChallengeDesc,
                icon: Target,
                cardBg:
                  'bg-[linear-gradient(145deg,#260505_0%,#570909_52%,#850E16_100%)]',
                activeBorder:
                  'border-[#FF3038] shadow-[0_0_28px_rgba(255,48,56,0.45),inset_0_1px_0_rgba(255,115,120,0.45)]',
                inactiveBorder:
                  'border-[#FF3038]/75 shadow-[0_0_14px_rgba(255,48,56,0.18)] md:hover:border-[#FF5A60] md:hover:shadow-[0_0_24px_rgba(255,48,56,0.35)]',
                iconContainer:
                  'bg-[linear-gradient(145deg,#7A0C12_0%,#260505_100%)] border-[#FF3038]/65 shadow-[inset_0_1px_1px_rgba(255,120,125,0.35),0_4px_12px_rgba(0,0,0,0.35)]',
                iconColor: 'text-[#FF4B52]',
                topGlow:
                  'bg-[radial-gradient(ellipse_at_top_left,rgba(255,75,82,0.26),transparent_65%)]',
                bottomGlow:
                  'bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,48,56,0.32),transparent_65%)]',
              },
              {
                id: 'ranked' as QuizPurposeMode,
                title: t.modeRanked,
                desc: t.modeRankedDesc,
                icon: Trophy,
                cardBg:
                  'bg-[linear-gradient(145deg,#241800_0%,#493000_52%,#634500_100%)]',
                activeBorder:
                  'border-[#E5AC00] shadow-[0_0_28px_rgba(229,172,0,0.42),inset_0_1px_0_rgba(255,215,0,0.45)]',
                inactiveBorder:
                  'border-[#E5AC00]/75 shadow-[0_0_14px_rgba(229,172,0,0.18)] md:hover:border-[#FFD700] md:hover:shadow-[0_0_24px_rgba(229,172,0,0.34)]',
                iconContainer:
                  'bg-[linear-gradient(145deg,#5C3F00_0%,#241800_100%)] border-[#E5AC00]/65 shadow-[inset_0_1px_1px_rgba(255,215,0,0.35),0_4px_12px_rgba(0,0,0,0.35)]',
                iconColor: 'text-[#FFD700]',
                topGlow:
                  'bg-[radial-gradient(ellipse_at_top_left,rgba(255,215,0,0.26),transparent_65%)]',
                bottomGlow:
                  'bg-[radial-gradient(ellipse_at_bottom_right,rgba(229,172,0,0.32),transparent_65%)]',
              },
            ] as const
          ).map((m) => {
            const Icon = m.icon;
            const active = gameMode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`game-mode-card-${m.id}`}
                onClick={() => handleSelectGameMode(m.id)}
                className={`group relative rounded-2xl border-2 p-5 sm:p-6 text-left transition-all duration-200 flex flex-col justify-start overflow-hidden cursor-pointer select-none ${
                  m.cardBg
                } ${active ? m.activeBorder : m.inactiveBorder}`}
              >
                {/* Ambient metallic top-left & bottom-right lighting */}
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-0 transition-opacity duration-200 ${
                    m.topGlow
                  } ${active ? 'opacity-100' : 'opacity-65 md:group-hover:opacity-90'}`}
                />
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-0 transition-opacity duration-200 ${
                    m.bottomGlow
                  } ${active ? 'opacity-100' : 'opacity-60 md:group-hover:opacity-85'}`}
                />

                {/* Top Row: Metallic Icon Container + Selected Green Checkmark */}
                <div className="relative z-10 flex items-start justify-between w-full mb-4">
                  <div
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl border flex items-center justify-center shrink-0 ${m.iconContainer}`}
                  >
                    <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${m.iconColor}`} />
                  </div>

                  {active && (
                    <span
                      data-testid={`game-mode-selected-${m.id}`}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#00D84A] flex items-center justify-center shadow-[0_0_12px_rgba(0,216,74,0.65)] shrink-0"
                    >
                      <Check className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white stroke-[3]" />
                    </span>
                  )}
                </div>

                {/* Title & Description */}
                <div className="relative z-10 min-w-0">
                  <div className="text-lg sm:text-xl font-extrabold text-white tracking-tight mb-1.5">
                    {m.title}
                  </div>
                  <div className="text-xs sm:text-sm text-white/85 leading-relaxed">
                    {m.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Configuration Form */}
        <div className="lg:col-span-2 rounded-2xl bg-[#141414] border border-[#262626] p-5 sm:p-8 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mb-6">
            <div>
              <label
                htmlFor="quiz-category-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-2"
              >
                {t.selectCategory}
              </label>
              <select
                id="quiz-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              >
                {BIBLE_CATEGORIES.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map((cat) => (
                      <option key={cat} value={cat}>
                        {getLocalizedCategoryLabel(cat, language)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="quiz-difficulty-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-2"
              >
                {t.selectDifficulty}
              </label>
              <select
                id="quiz-difficulty-select"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              >
                {DIFFICULTY_LEVELS.map((diff) => (
                  <option key={diff} value={diff}>
                    {getLocalizedDifficultyLabel(diff, language)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="quiz-format-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-2"
              >
                {t.selectFormat}
              </label>
              <select
                id="quiz-format-select"
                value={questionFormat}
                onChange={(e) => setQuestionFormat(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              >
                {availableFormats.map((fmt) => (
                  <option key={fmt} value={fmt}>
                    {getLocalizedFormatLabel(fmt, language)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="quiz-count-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-2"
              >
                {t.questionCount}
              </label>
              <select
                id="quiz-count-select"
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value))}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              >
                <option value={5}>5 {t.questionCount}</option>
                <option value={8}>8 {t.questionCount}</option>
                <option value={10}>10 {t.questionCount}</option>
                <option value={12}>12 {t.questionCount}</option>
              </select>
            </div>
          </div>

          {/* Toggles Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-4 pb-6 border-t border-[#262626]">
            <label className="flex items-center justify-between p-3.5 rounded-xl border border-[#262626] cursor-pointer">
              <div>
                <div className="text-xs font-semibold text-[#FAFAFA]">
                  {t.enableTimer}
                </div>
                <div className="text-[11px] text-[#737373]">
                  {gameMode === 'ranked'
                    ? `20s (${t.modeRanked})`
                    : `${timerSeconds}s`}
                </div>
              </div>
              <input
                type="checkbox"
                disabled={gameMode === 'ranked'}
                checked={timerEnabled}
                onChange={(e) => setTimerEnabled(e.target.checked)}
                className="w-4 h-4 accent-[#525252] rounded"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl border border-[#262626] cursor-pointer">
              <div>
                <div className="text-xs font-semibold text-[#FAFAFA]">
                  {t.enableNarration}
                </div>
                <div className="text-[11px] text-[#737373]">
                  {language.toUpperCase()} TTS
                </div>
              </div>
              <input
                type="checkbox"
                checked={narrationEnabled}
                onChange={(e) => {
                  setNarrationEnabled(e.target.checked);
                  soundEngine.narrationEnabled = e.target.checked;
                }}
                className="w-4 h-4 accent-[#525252] rounded"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl border border-[#262626] cursor-pointer">
              <div>
                <div className="text-xs font-semibold text-[#FAFAFA]">
                  {t.narrateChoicesLabel}
                </div>
                <div className="text-[11px] text-[#737373]">
                  {t.narrateInstructionsLabel}
                </div>
              </div>
              <input
                type="checkbox"
                checked={narrateChoices}
                onChange={(e) => {
                  setNarrateChoices(e.target.checked);
                  setNarrateInstructions(e.target.checked);
                  soundEngine.narrateChoices = e.target.checked;
                  soundEngine.narrateInstructions = e.target.checked;
                }}
                className="w-4 h-4 accent-[#525252] rounded"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl border border-[#262626] cursor-pointer">
              <div>
                <div className="text-xs font-semibold text-[#FAFAFA]">
                  {t.freshPoolLabel}
                </div>
                <div className="text-[11px] text-[#737373]">
                  {t.freshPoolDesc}
                </div>
              </div>
              <input
                type="checkbox"
                checked={forceFreshBatch}
                onChange={(e) => setForceFreshBatch(e.target.checked)}
                className="w-4 h-4 accent-[#525252] rounded"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={() => startQuizSession()}
            data-start="true"
            className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-orange-950/30 transition-all cursor-pointer"
          >
            <Play className="w-4 h-4 fill-white" />
            <span>{t.generateAndPlay}</span>
          </button>
        </div>

        {/* Special Quiz Modes Quick-Launch */}
        <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h2 className="text-base font-bold text-[#FAFAFA] mb-1">
              {t.specialModesTitle}
            </h2>
            <p className="text-xs text-[#737373] text-[#A3A3A3] mb-4">
              {t.freshPoolDesc}
            </p>

            <div className="space-y-2.5">
              {[
                {
                  name: t.dailyChallenge,
                  desc: t.dailySubtitle,
                  fmt: 'Automatic / Mixed',
                  cat: 'ALL CATEGORIES',
                  sub: 'daily',
                },
                {
                  name: getLocalizedFormatLabel('Multiple Choice', language),
                  desc: getLocalizedCategoryLabel('ALL CATEGORIES', language),
                  fmt: 'Multiple Choice',
                  cat: 'ALL CATEGORIES',
                  sub: 'mc',
                },
                {
                  name: getLocalizedFormatLabel('True or False', language),
                  desc: getLocalizedCategoryLabel('ALL CATEGORIES', language),
                  fmt: 'True or False',
                  cat: 'ALL CATEGORIES',
                  sub: 'tf',
                },
                {
                  name: getLocalizedFormatLabel('Fill in the Blank', language),
                  desc: getLocalizedCategoryLabel(
                    'Psalms and Proverbs',
                    language
                  ),
                  fmt: 'Fill in the Blank',
                  cat: 'Psalms and Proverbs',
                  sub: 'fill',
                },
                {
                  name: getLocalizedFormatLabel('Short Answer', language),
                  desc: getLocalizedCategoryLabel('The Four Gospels', language),
                  fmt: 'Short Answer',
                  cat: 'The Four Gospels',
                  sub: 'short',
                },
                {
                  name: getLocalizedFormatLabel('Matching', language),
                  desc: getLocalizedCategoryLabel(
                    'Biblical Characters',
                    language
                  ),
                  fmt: 'Matching',
                  cat: 'ALL CATEGORIES',
                  sub: 'matching',
                },
              ].map((m) => (
                <button
                  key={m.sub}
                  type="button"
                  onClick={() => {
                    setCategory(m.cat);
                    setQuestionFormat(m.fmt);
                    startQuizSession({
                      overrideCategory: m.cat,
                      overrideFormat: m.fmt,
                      overrideMode: m.sub === 'daily' ? 'daily' : gameMode,
                      overrideSubMode: m.sub,
                    });
                  }}
                  className="w-full p-3 rounded-xl border border-[#262626] hover:border-[#525252] text-left transition-colors flex items-center justify-between gap-3"
                >
                  <div>
                    <div className="text-xs font-semibold text-[#FAFAFA]">
                      {m.name}
                    </div>
                    <div className="text-[11px] text-[#737373] text-[#A3A3A3]">
                      {m.desc}
                    </div>
                  </div>
                  <Sparkles className="w-4 h-4 text-[#A3A3A3] shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AdSenseBanner placement="quiz-setup" className="mt-8" />
    </div>
  );
};
